import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { cacClaimsApi, PendingCacClaim } from '../../api/cac-claims';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';

/**
 * CAC numbers claimed by somebody the filing does not name.
 *
 * ## What the reviewer is actually deciding
 *
 * Not "does this company exist" — the register already answered that. The
 * question is whether **this person runs it**, and it is being asked because
 * an RC number is public: it is printed on invoices and letterheads and the
 * CAC portal is searchable. Everything else at Tier 1 proves the applicant is
 * a real person; nothing else proves they are connected to this company.
 * Safe Haven will not catch it — it verifies a director and accepts the
 * registration number as data.
 *
 * ## Most of these are honest
 *
 * Registers lag behind director changes. Names are filed differently from
 * how people write them, and married names differ from filed ones. A company
 * whose listed director is another company can never match a human name at
 * all — that is a review case by construction rather than a failure.
 *
 * So this is laid out as a comparison rather than an accusation: who claimed
 * it, beside who is on the filing, and nothing implying which way to go.
 *
 * ## What approving does
 *
 * Stamps the registry check, which is the only thing standing between this
 * business and a corporate account. There is no second step and no other
 * gate behind it.
 */
const cell: React.CSSProperties = {
  padding: '12px 20px',
  borderBottom: '1px solid var(--neutral-200)',
  verticalAlign: 'top',
};

export const CacClaimQueue: React.FC = () => {
  usePage('CAC claims');
  const toast = useToast();
  const [pending, setPending] = useState<PendingCacClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const fetchPending = async () => {
    try {
      setLoading(true);
      setError(null);
      setPending(await cacClaimsApi.pending());
    } catch {
      setError('Failed to load the CAC claim queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPending();
  }, []);

  const decide = async (
    entry: PendingCacClaim,
    decision: 'approve' | 'reject',
  ) => {
    const note = notes[entry.id]?.trim();
    if (!note) {
      // Both ways need one. Approving opens a corporate account in a
      // company's name; rejecting tells somebody who may genuinely run that
      // company that they cannot have an account.
      toast.error('Say what you checked, before deciding either way.');
      return;
    }

    try {
      setBusyId(entry.id);
      await cacClaimsApi.review(entry.id, decision, note);
      toast.success(
        decision === 'approve'
          ? 'Approved — the account can now be opened.'
          : 'Rejected. The registration stays unverified.',
      );
      await fetchPending();
    } catch (err) {
      toast.error(err, 'Could not record that decision.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Card padding={0}>
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--neutral-300)',
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Awaiting review
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            The company exists — the register said so. What is unresolved is
            whether the person claiming it runs it. Approving is what lets the
            account be opened.
          </p>
        </div>

        {error && (
          <div
            style={{
              margin: 20,
              padding: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-900)',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {error}
          </div>
        )}

        {!error && pending.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: 'var(--neutral-500)',
              fontSize: 13,
            }}
          >
            Nothing waiting. Claims are matching their filings.
          </div>
        )}

        {pending.map((entry) => (
          <div key={entry.id} style={cell}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                marginBottom: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
                  {entry.companyName}
                </div>
                <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  {entry.registrationNumber} · claimed{' '}
                  {format(new Date(entry.createdAt), 'd MMM, HH:mm')}
                </div>
              </div>
              <a
                href={`/businesses/${entry.businessId}/kyb`}
                style={{ fontSize: 13, color: 'var(--blue-600)', whiteSpace: 'nowrap' }}
              >
                Open the business
              </a>
            </div>

            {/* The comparison, side by side. Laid out as two lists rather
                than a verdict, because the register being out of date is at
                least as likely as the claim being wrong. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
                background: 'var(--neutral-100)',
                borderRadius: 'var(--radius-md)',
                padding: 14,
                fontSize: 13,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginBottom: 4 }}>
                  CLAIMED BY
                </div>
                <div style={{ color: 'var(--neutral-800)' }}>
                  {entry.claimedByName ?? 'Unnamed'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginTop: 2 }}>
                  BVN verified
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginBottom: 4 }}>
                  ON THE CAC FILING
                </div>
                {entry.filedDirectors.length === 0 ? (
                  /* No human names at all — usually a company owned by
                     another company, which can never match by construction. */
                  <div style={{ color: 'var(--neutral-500)' }}>
                    Nobody listed on the filing
                  </div>
                ) : (
                  entry.filedDirectors.map((d) => (
                    <div key={d.fullName} style={{ color: 'var(--neutral-800)' }}>
                      {d.fullName}
                      {d.boardRole ? (
                        <span style={{ color: 'var(--neutral-500)' }}>
                          {' '}
                          ({d.boardRole.toLowerCase()})
                        </span>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 10,
                marginTop: 12,
                alignItems: 'center',
              }}
            >
              <input
                value={notes[entry.id] ?? ''}
                onChange={(e) =>
                  setNotes({ ...notes, [entry.id]: e.target.value })
                }
                placeholder="What you checked"
                style={{
                  flex: 1,
                  height: 34,
                  padding: '0 10px',
                  fontSize: 13,
                  fontFamily: 'var(--font-family)',
                  color: 'var(--neutral-800)',
                  background: 'var(--neutral-100)',
                  border: '1px solid var(--neutral-300)',
                  borderRadius: 'var(--radius-md)',
                  outline: 'none',
                }}
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={busyId === entry.id}
                onClick={() => void decide(entry, 'reject')}
              >
                Not their company
              </Button>
              <Button
                size="sm"
                disabled={busyId === entry.id}
                onClick={() => void decide(entry, 'approve')}
              >
                They run it
              </Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};
